import React, { useMemo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Formik } from 'formik';
import { Form, Select } from 'formik-antd';
import { Col, Row, message, Upload } from 'antd';
import {
	PlusOutlined,
	CloseOutlined,
	MinusOutlined,
	UploadOutlined,
	EyeOutlined,
	FilePdfOutlined,
} from '@ant-design/icons';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, FormInput, CustomInput, Text } from 'atoms';
import { createClientV2, updateClientV2, searchPincodeV2 } from 'store/clientSliceV2';
import { getSignedUrl } from 'store/commonSlice';
import { DocumentViewerModal } from 'componentsv2/common/modals';
import {
	FormContainer,
	SectionTitle,
	BillingAddressBox,
	FileItem,
	DisabledInput,
	DocumentTypeInput,
	AddButton,
	AddDocumentButton,
	BrowseButton,
	CRFBrowseButton,
	PreviousButton,
	SaveButton,
	CancelButton,
	NextButton,
} from './clientFormStyles';
import {
	BusinessInfoSchema,
	GST_TYPE_OPTIONS,
	EMPTY_BILLING_ADDRESS,
	EMPTY_ADDITIONAL_DOCUMENT,
	ADDITIONAL_DOCUMENT_TYPE_OPTIONS,
	CLIENT_LIST_ROUTE,
} from './clientFormConstants';

// Constants
const UPLOAD_BUTTON_TEXT = 'Browse File';
const UPLOADING_TEXT = 'Uploading...';

/**
 * Extract filename from S3 key by removing UUID prefix
 * S3 keys are in format: {uuid}-{filename}
 * Example: "340d1b25-c372-439e-b92c-5306e4748a3-Data_Analytics_Assignment.pdf"
 * Returns: "Data_Analytics_Assignment.pdf"
 */
const extractFilenameFromS3Key = (s3Key) => {
	if (!s3Key) return '';
	// UUID format: 8-4-4-4-12 characters (36 chars total + 1 hyphen)
	// Find the position after the UUID prefix (first 37 characters)
	const uuidPattern = /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}-/i;
	const match = s3Key.match(uuidPattern);
	if (match) {
		return s3Key.substring(match[0].length);
	}
	// If no UUID pattern found, return the original key
	return s3Key;
};

export default function ClientBusinessInfoFormV2({
	basicInfoData,
	businessInfoData,
	setBusinessInfoData,
	setCurrentStep,
	isEditMode,
	isViewMode,
	onCancel,
}) {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { id } = useParams();

	// File states
	const [panFile, setPanFile] = useState(null);
	const [gstFile, setGstFile] = useState(null);
	const [crfFile, setCrfFile] = useState(null);

	// File key states (S3 keys after upload)
	const [panFileKey, setPanFileKey] = useState('');
	const [gstFileKey, setGstFileKey] = useState('');
	const [crfFileKey, setCrfFileKey] = useState('');

	// Upload loading states
	const [panUploading, setPanUploading] = useState(false);
	const [gstUploading, setGstUploading] = useState(false);
	const [crfUploading, setCrfUploading] = useState(false);

	// View loading states
	const [panViewing, setPanViewing] = useState(false);
	const [gstViewing, setGstViewing] = useState(false);
	const [crfViewing, setCrfViewing] = useState(false);

	// Additional documents state
	const [additionalDocFiles, setAdditionalDocFiles] = useState({});
	const [additionalDocFileKeys, setAdditionalDocFileKeys] = useState({});
	const [additionalDocUploading, setAdditionalDocUploading] = useState({});
	const [additionalDocViewing, setAdditionalDocViewing] = useState({});

	// Document viewer modal state
	const [documentViewerVisible, setDocumentViewerVisible] = useState(false);
	const [currentDocumentUrl, setCurrentDocumentUrl] = useState('');
	const [currentDocumentName, setCurrentDocumentName] = useState('');

	// Helper: Initialize additional documents file state
	const initializeAdditionalDocFiles = (documents) => {
		if (!documents || !Array.isArray(documents)) {
			return;
		}

		const additionalFiles = {};
		const additionalKeys = {};
		documents.forEach((doc, index) => {
			if (doc.document_file_key) {
				additionalFiles[index] = { name: doc.document_file_key };
				additionalKeys[index] = doc.document_file_key;
			}
		});

		// Only update if there are file keys to preserve, otherwise keep existing state
		if (Object.keys(additionalKeys).length > 0) {
			setAdditionalDocFiles(additionalFiles);
			setAdditionalDocFileKeys(additionalKeys);
		}
	};

	// Initialize file keys and file objects from businessInfoData
	useEffect(() => {
		if (!businessInfoData) {
			return;
		}

		// Set file keys for standard documents (only if they exist in businessInfoData)
		if (businessInfoData.pan_file_key) {
			setPanFileKey(businessInfoData.pan_file_key);
			setPanFile({ name: businessInfoData.pan_file_key });
		}
		if (businessInfoData.gst_file_key) {
			setGstFileKey(businessInfoData.gst_file_key);
			setGstFile({ name: businessInfoData.gst_file_key });
		}
		if (businessInfoData.crf_file_key) {
			setCrfFileKey(businessInfoData.crf_file_key);
			setCrfFile({ name: businessInfoData.crf_file_key });
		}

		// Initialize additional documents files
		initializeAdditionalDocFiles(businessInfoData.additional_documents);
	}, [businessInfoData]);

	// Business Info Initial Values
	const initialValues = useMemo(() => {
		// Ensure additional documents have file_uploaded flags set correctly
		const additionalDocuments =
			businessInfoData?.additional_documents?.map((doc) => ({
				...doc,
				file_uploaded: !!doc.document_file_key,
			})) || [];

		return {
			billing_addresses: businessInfoData?.billing_addresses || [EMPTY_BILLING_ADDRESS],
			pan_number: businessInfoData?.pan_number || '',
			gst_type: businessInfoData?.gst_type || undefined,
			gst_number: businessInfoData?.gst_number || '',
			credit_period: businessInfoData?.credit_period || '',
			crf_number: businessInfoData?.crf_file_key
				? extractFilenameFromS3Key(businessInfoData.crf_file_key)
				: businessInfoData?.crf_number || '',
			// File upload validation flags
			pan_file_uploaded: !!businessInfoData?.pan_file_key,
			gst_file_uploaded: !!businessInfoData?.gst_file_key,
			crf_file_uploaded: !!businessInfoData?.crf_file_key,
			// Additional documents with file_uploaded flags
			additional_documents: additionalDocuments,
		};
	}, [businessInfoData]);

	// Helper: Set uploading state for document type
	const setUploadingState = (documentType, isUploading, docIndex) => {
		if (documentType === 'pan') setPanUploading(isUploading);
		else if (documentType === 'gst') setGstUploading(isUploading);
		else if (documentType === 'crf') setCrfUploading(isUploading);
		else if (documentType === 'additional' && docIndex !== undefined) {
			setAdditionalDocUploading((prev) => ({ ...prev, [docIndex]: isUploading }));
		}
	};

	// Helper: Store uploaded file data
	const storeFileData = (documentType, fileData, fileType, fileSize, setFieldValue, docIndex) => {
		const fileInfo = { name: fileData.filename, type: fileType, size: fileSize };

		if (documentType === 'pan') {
			setPanFile(fileInfo);
			setPanFileKey(fileData.filename);
			if (setFieldValue) setFieldValue('pan_file_uploaded', true);
		} else if (documentType === 'gst') {
			setGstFile(fileInfo);
			setGstFileKey(fileData.filename);
			if (setFieldValue) setFieldValue('gst_file_uploaded', true);
		} else if (documentType === 'crf') {
			setCrfFile(fileInfo);
			setCrfFileKey(fileData.filename);
			if (setFieldValue) {
				setFieldValue('crf_file_uploaded', true);
				// Set only the filename (without UUID prefix) in the crf_number field
				setFieldValue('crf_number', extractFilenameFromS3Key(fileData.filename));
			}
		} else if (documentType === 'additional' && docIndex !== undefined) {
			// Store additional document file
			setAdditionalDocFiles((prev) => ({ ...prev, [docIndex]: fileInfo }));
			setAdditionalDocFileKeys((prev) => ({ ...prev, [docIndex]: fileData.filename }));
			if (setFieldValue) {
				setFieldValue(`additional_documents[${docIndex}].file_uploaded`, true);
				setFieldValue(
					`additional_documents[${docIndex}].document_file_key`,
					fileData.filename
				);
				// Note: document_number should be entered manually by the user, not auto-populated from filename
			}
		}
	};

	/**
	 * Handle file upload with signed URL
	 * @param {File} file - File to upload
	 * @param {string} documentType - Type of document (pan/gst/crf/additional)
	 * @param {Function} setFieldValue - Formik setFieldValue function
	 * @param {number} docIndex - Index for additional documents
	 * @returns {Promise} Upload result
	 */
	const handleFileUpload = async (file, documentType, setFieldValue, docIndex) => {
		if (!file) {
			message.error('Please select a file');
			return false;
		}

		// File validation
		const allowedTypes = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg'];
		const maxSizeMB = 5;

		if (!allowedTypes.includes(file.type)) {
			message.error('You can only upload PDF, PNG, JPG, JPEG files!');
			return false;
		}
		if (file.size / 1024 / 1024 > maxSizeMB) {
			message.error(`File must be smaller than ${maxSizeMB}MB!`);
			return false;
		}

		setUploadingState(documentType, true, docIndex);

		try {
			// Step 1: Get signed URL
			const signedUrlResponse = await dispatch(
				getSignedUrl({
					url_type: 'upload',
					data: {
						entity: 'client',
						keys: [file.name],
					},
				})
			);

			const fileData = signedUrlResponse?.data?.[0];
			if (!fileData?.url || !fileData?.filename) {
				throw new Error('Failed to get signed URL');
			}

			// Step 2: Upload file to S3
			await fetch(fileData.url, {
				method: 'PUT',
				body: file,
				headers: {
					'Content-Type': file.type,
				},
			});

			// Step 3: Store file data
			storeFileData(documentType, fileData, file.type, file.size, setFieldValue, docIndex);

			message.success(
				`${
					documentType === 'additional' ? 'Document' : documentType.toUpperCase()
				} file uploaded successfully`
			);
			return true;
		} catch (error) {
			console.error('File upload error:', error);
			message.error(
				`Failed to upload ${
					documentType === 'additional' ? 'document' : documentType.toUpperCase()
				} file`
			);
			return false;
		} finally {
			setUploadingState(documentType, false, docIndex);
		}
	};

	// Helper: Set viewing state for document
	const setViewingState = (documentType, docIndex, isViewing) => {
		if (documentType === 'pan') setPanViewing(isViewing);
		else if (documentType === 'gst') setGstViewing(isViewing);
		else if (documentType === 'crf') setCrfViewing(isViewing);
		else if (documentType === 'additional' && docIndex !== undefined) {
			setAdditionalDocViewing((prev) => ({ ...prev, [docIndex]: isViewing }));
		}
	};

	// Helper: Process document view response
	const processViewResponse = (response, documentName = 'Document') => {
		if (response?.data && Array.isArray(response.data) && response.data.length > 0) {
			const viewUrl = response.data[0].url;
			if (viewUrl) {
				// Open document in modal instead of new tab
				setCurrentDocumentUrl(viewUrl);
				setCurrentDocumentName(documentName);
				setDocumentViewerVisible(true);
			} else {
				message.error('Document URL not found in response');
			}
		} else {
			console.error('Unexpected response format:', response);
			message.error('Failed to get document URL');
		}
	};

	/**
	 * Handle viewing document by getting signed URL
	 * @param {string} fileKey - S3 file key
	 * @param {string} documentType - Type of document (pan/gst/crf/additional)
	 * @param {number} docIndex - Index for additional documents
	 * @returns {Promise} View result
	 */
	const handleViewDocument = async (fileKey, documentType, docIndex) => {
		if (!fileKey) {
			message.error('Document not available');
			return;
		}

		// Generate document name based on type
		let documentName = 'Document';
		if (documentType === 'pan') documentName = 'PAN Document';
		else if (documentType === 'gst') documentName = 'GST Document';
		else if (documentType === 'crf') documentName = 'CRF Document';
		else if (documentType === 'additional' && docIndex !== undefined) {
			documentName = `Additional Document ${docIndex + 1}`;
		}

		setViewingState(documentType, docIndex, true);

		try {
			const response = await dispatch(
				getSignedUrl({
					url_type: 'view',
					data: {
						entity: 'client',
						keys: [fileKey],
					},
				})
			);

			processViewResponse(response, documentName);
		} catch (error) {
			console.error('Error viewing document:', error);
			message.error('Failed to view document');
		} finally {
			setViewingState(documentType, docIndex, false);
		}
	};

	// Handle pincode lookup for billing address - Auto-fetch zone, city, state when user types 6-digit pincode
	const handleBillingPincodeLookup = (pincode, index, setFieldValue) => {
		if (pincode?.length === 6) {
			dispatch(searchPincodeV2({ pincode })).then((res) => {
				if (res) {
					setFieldValue(`billing_addresses[${index}].zone`, res.zone || '');
					setFieldValue(`billing_addresses[${index}].city`, res.city || '');
					setFieldValue(`billing_addresses[${index}].state`, res.state || '');
				} else {
					// Clear fields if pincode not found
					setFieldValue(`billing_addresses[${index}].zone`, '');
					setFieldValue(`billing_addresses[${index}].city`, '');
					setFieldValue(`billing_addresses[${index}].state`, '');
				}
			});
		} else if (pincode?.length < 6) {
			// Clear fields if user is still typing or deleted characters
			setFieldValue(`billing_addresses[${index}].zone`, '');
			setFieldValue(`billing_addresses[${index}].city`, '');
			setFieldValue(`billing_addresses[${index}].state`, '');
		}
	};

	// Handle form submit
	const handleSubmit = async (values, { setSubmitting, setErrors }) => {
		// Ensure additional documents have their file keys before saving to state
		const additionalDocumentsWithFileKeys =
			values.additional_documents?.map((doc, index) => {
				const fileKey = additionalDocFileKeys[index] || doc.document_file_key;
				return {
					...doc,
					document_file_key: fileKey || doc.document_file_key,
				};
			}) || [];

		// Save form data with file keys to preserve state after failed submission
		const dataToSave = {
			...values,
			additional_documents: additionalDocumentsWithFileKeys,
			// Include file keys so validation flags persist after failed submission
			pan_file_key: panFileKey,
			gst_file_key: gstFileKey,
			crf_file_key: crfFileKey,
		};
		setBusinessInfoData(dataToSave);

		// File validation is now handled by Formik schema
		// No need for manual validation here

		try {
			// Build the complete payload with file keys
			const req_data = {
				...basicInfoData,
				...values,
				additional_documents: additionalDocumentsWithFileKeys,
				document_info: {
					pan_file: panFile,
					gst_file: gstFile,
					crf_file: crfFile,
				},
				// Add file keys for API payload
				pan_file_key: panFileKey,
				gst_file_key: gstFileKey,
				crf_file_key: crfFileKey,
			};

			let response;
			if (isEditMode && id) {
				// Update existing client
				response = await dispatch(
					updateClientV2({
						req_data: {
							...req_data,
							id,
						},
						setErrors,
					})
				);
			} else {
				// Create new client
				response = await dispatch(
					createClientV2({
						req_data,
						setErrors,
					})
				);
			}

			if (response?.error || response?.status === 'error') {
				const errorMessage =
					response?.error?.message ||
					response?.message ||
					(isEditMode ? 'Failed to update client' : 'Failed to create client');
				message.error(errorMessage);
				return;
			}

			// Navigate back to list on success
			navigate(CLIENT_LIST_ROUTE);
		} catch (error) {
			console.error('Error saving client:', error);
		} finally {
			setSubmitting(false);
		}
	};

	// Add new billing address
	const addBillingAddress = (values, setFieldValue) => {
		const newAddresses = [...values.billing_addresses, EMPTY_BILLING_ADDRESS];
		setFieldValue('billing_addresses', newAddresses);
	};

	// Remove billing address
	const removeBillingAddress = (index, values, setFieldValue) => {
		const newAddresses = values.billing_addresses.filter((_, i) => i !== index);
		setFieldValue('billing_addresses', newAddresses);
	};

	// Add new additional document
	const addAdditionalDocument = (values, setFieldValue) => {
		// Check if there are already 2 documents
		const currentDocumentCount = values.additional_documents?.length || 0;

		if (currentDocumentCount >= 2) {
			message.info('Maximum 2 additional documents allowed');
			return;
		}

		const newDocuments = [...values.additional_documents, EMPTY_ADDITIONAL_DOCUMENT];
		setFieldValue('additional_documents', newDocuments);
	};

	// Remove additional document
	const removeAdditionalDocument = (index, values, setFieldValue) => {
		const newDocuments = values.additional_documents.filter((_, i) => i !== index);
		setFieldValue('additional_documents', newDocuments);
		// Clean up file states for this index
		setAdditionalDocFiles((prev) => {
			const updated = { ...prev };
			delete updated[index];
			return updated;
		});
		setAdditionalDocFileKeys((prev) => {
			const updated = { ...prev };
			delete updated[index];
			return updated;
		});
	};

	// File upload props with S3 upload
	const getUploadProps = (documentType, setFieldValue, docIndex) => ({
		beforeUpload: async (file) => {
			await handleFileUpload(file, documentType, setFieldValue, docIndex);
			return false; // Prevent default upload behavior
		},
		showUploadList: false,
		accept: '.pdf,.png,.jpg,.jpeg',
	});

	// Helper: Remove additional document file
	const removeAdditionalDocFile = (docIndex, setFieldValue) => {
		setAdditionalDocFiles((prev) => {
			const updated = { ...prev };
			delete updated[docIndex];
			return updated;
		});
		setAdditionalDocFileKeys((prev) => {
			const updated = { ...prev };
			delete updated[docIndex];
			return updated;
		});
		if (setFieldValue) {
			setFieldValue(`additional_documents[${docIndex}].file_uploaded`, false);
			setFieldValue(`additional_documents[${docIndex}].document_file_key`, '');
			setFieldValue(`additional_documents[${docIndex}].document_number`, '');
		}
	};

	// Helper: Remove regular document file
	const removeRegularDocFile = (documentType, setFile, setFileKey, setFieldValue) => {
		setFile(null);
		setFileKey('');
		if (setFieldValue) {
			if (documentType === 'pan') setFieldValue('pan_file_uploaded', false);
			if (documentType === 'gst') setFieldValue('gst_file_uploaded', false);
			if (documentType === 'crf') {
				setFieldValue('crf_file_uploaded', false);
				setFieldValue('crf_number', '');
			}
		}
	};

	// Helper: Handle file removal for different document types
	const handleDocumentRemoval = (documentType, setFile, setFileKey, setFieldValue, docIndex) => {
		if (documentType === 'additional' && docIndex !== undefined) {
			removeAdditionalDocFile(docIndex, setFieldValue);
		} else {
			removeRegularDocFile(documentType, setFile, setFileKey, setFieldValue);
		}
	};

	// Helper: Render view mode document section
	const renderViewModeDocument = (fileKey, file, viewing, documentType, docIndex) => {
		if (fileKey) {
			const filename = extractFilenameFromS3Key(fileKey);
			// Get file size from file object if available
			const fileSize = file?.size ? `${(file.size / (1024 * 1024)).toFixed(1)}mb` : '';

			return (
				<FileItem
					style={{ cursor: 'pointer' }}
					onClick={() => handleViewDocument(fileKey, documentType, docIndex)}
				>
					<Box display="flex" alignItems="center" gap={8}>
						<FilePdfOutlined style={{ color: '#ff4d4f', fontSize: '16px' }} />
						<Text fontSize={12} color="#1890ff">
							{filename}
						</Text>
						{fileSize && (
							<Text fontSize={12} color="#666">
								{fileSize}
							</Text>
						)}
					</Box>
					<EyeOutlined
						style={{
							cursor: 'pointer',
							color: '#18405d',
							fontSize: '16px',
						}}
					/>
				</FileItem>
			);
		}
		return (
			<Text fontSize={12} color="#999">
				No document uploaded
			</Text>
		);
	};

	// Helper: Render edit mode document section
	const renderEditModeDocument = (
		documentType,
		file,
		uploading,
		isDisabled,
		setFile,
		setFileKey,
		setFieldValue,
		docIndex,
		fileKey
	) => {
		const ButtonComponent = documentType === 'crf' ? CRFBrowseButton : BrowseButton;
		return (
			<Box display="flex" alignItems="center" gap={8}>
				<Upload
					{...getUploadProps(documentType, setFieldValue, docIndex)}
					disabled={uploading || isDisabled}
				>
					<ButtonComponent
						type="button"
						icon={<UploadOutlined />}
						disabled={uploading || isDisabled}
					>
						{uploading ? UPLOADING_TEXT : UPLOAD_BUTTON_TEXT}
					</ButtonComponent>
				</Upload>
				{file && (
					<FileItem>
						<Box display="flex" alignItems="center" gap={8}>
							<FilePdfOutlined style={{ color: '#ff4d4f', fontSize: '16px' }} />
							<Text fontSize={12} color="#1890ff">
								{extractFilenameFromS3Key(file.name)}
							</Text>
							<Text fontSize={12} color="#666">
								{file.size ? `${(file.size / (1024 * 1024)).toFixed(1)}mb` : ''}
							</Text>
						</Box>
						<Box display="flex" alignItems="center" gap={8}>
							{fileKey && (
								<EyeOutlined
									style={{
										cursor: 'pointer',
										color: '#18405d',
										fontSize: '16px',
									}}
									onClick={(e) => {
										e.stopPropagation();
										handleViewDocument(fileKey, documentType, docIndex);
									}}
								/>
							)}
							<CloseOutlined
								style={{ cursor: 'pointer', color: '#ff4d4f' }}
								onClick={() =>
									handleDocumentRemoval(
										documentType,
										setFile,
										setFileKey,
										setFieldValue,
										docIndex
									)
								}
							/>
						</Box>
					</FileItem>
				)}
			</Box>
		);
	};

	// Helper: Render document view/upload section
	const renderDocumentSection = (
		documentType,
		fileKey,
		file,
		viewing,
		uploading,
		setFile,
		setFileKey,
		setFieldValue,
		docIndex,
		isDisabled = false
	) => {
		if (isViewMode) {
			return renderViewModeDocument(fileKey, file, viewing, documentType, docIndex);
		}

		return renderEditModeDocument(
			documentType,
			file,
			uploading,
			isDisabled,
			setFile,
			setFileKey,
			setFieldValue,
			docIndex,
			fileKey
		);
	};

	// Helper: Render billing addresses section
	const renderBillingAddresses = (values, setFieldValue) =>
		values.billing_addresses?.map((address, index) => (
			<BillingAddressBox
				// eslint-disable-next-line react/no-array-index-key
				key={`billing-address-${index}`}
			>
				<Row gutter={24}>
					<Col span={12}>
						<FormInput
							label="Address line 1*"
							name={`billing_addresses[${index}].address_line1`}
						>
							<CustomInput
								name={`billing_addresses[${index}].address_line1`}
								placeholder="Eg : Intelligent Retail...."
								disabled={isViewMode}
							/>
						</FormInput>
					</Col>
					<Col span={values.billing_addresses.length > 1 && !isViewMode ? 11 : 12}>
						<FormInput
							label="Address line 2"
							name={`billing_addresses[${index}].address_line2`}
						>
							<CustomInput
								name={`billing_addresses[${index}].address_line2`}
								placeholder="Eg : Intelligent Retail...."
								disabled={isViewMode}
							/>
						</FormInput>
					</Col>
					{values.billing_addresses.length > 1 && !isViewMode && (
						<Col span={1}>
							<CloseOutlined
								style={{
									cursor: 'pointer',
									marginTop: 32,
									color: '#ff4d4f',
								}}
								onClick={() => removeBillingAddress(index, values, setFieldValue)}
							/>
						</Col>
					)}
				</Row>

				<Row gutter={24}>
					<Col span={6}>
						<FormInput label="Pincode*" name={`billing_addresses[${index}].pincode`}>
							<CustomInput
								name={`billing_addresses[${index}].pincode`}
								placeholder="517408"
								disabled={isViewMode}
								onChange={(e) => {
									setFieldValue(
										`billing_addresses[${index}].pincode`,
										e.target.value
									);
									handleBillingPincodeLookup(
										e.target.value,
										index,
										setFieldValue
									);
								}}
							/>
						</FormInput>
					</Col>
					<Col span={6}>
						<FormInput label="Zone" name={`billing_addresses[${index}].zone`}>
							<DisabledInput
								name={`billing_addresses[${index}].zone`}
								placeholder="South"
								disabled
							/>
						</FormInput>
					</Col>
					<Col span={6}>
						<FormInput label="City" name={`billing_addresses[${index}].city`}>
							<DisabledInput
								name={`billing_addresses[${index}].city`}
								placeholder="Bangalore"
								disabled
							/>
						</FormInput>
					</Col>
					<Col span={6}>
						<FormInput label="State" name={`billing_addresses[${index}].state`}>
							<DisabledInput
								name={`billing_addresses[${index}].state`}
								placeholder="Karnataka"
								disabled
							/>
						</FormInput>
					</Col>
				</Row>
			</BillingAddressBox>
		));

	// Helper: Render business documents section
	const renderBusinessDocuments = (values, setFieldValue) => (
		<>
			<SectionTitle>Proof of Business Documents</SectionTitle>

			{/* PAN Document */}
			<Row gutter={24} align="middle">
				<Col span={5}>
					<FormInput label="Document Type*" name="pan_doc_type">
						<DocumentTypeInput
							value="PAN"
							disabled
							style={{ background: '#C6C6C8', color: '#fff' }}
						/>
					</FormInput>
				</Col>
				<Col span={10}>
					<FormInput label="PAN*" name="pan_number">
						<CustomInput
							name="pan_number"
							placeholder="Enter Pan Number"
							disabled={isViewMode}
						/>
					</FormInput>
				</Col>
				<Col span={9}>
					<FormInput name="pan_file_uploaded" required style={{ marginTop: 0 }}>
						<Box mt={36}>
							{renderDocumentSection(
								'pan',
								panFileKey,
								panFile,
								panViewing,
								panUploading,
								setPanFile,
								setPanFileKey,
								setFieldValue,
								undefined
							)}
						</Box>
					</FormInput>
				</Col>
			</Row>

			{/* GST Document */}
			<Row gutter={24} align="middle">
				<Col span={4}>
					<FormInput label="Document Type*" name="gst_doc_type">
						<DocumentTypeInput
							value="GST"
							disabled
							style={{ background: '#C6C6C8', color: '#fff' }}
						/>
					</FormInput>
				</Col>
				<Col span={4}>
					<FormInput label="GST Type*" name="gst_type">
						<Select
							name="gst_type"
							value={values.gst_type}
							placeholder="Select GST Type"
							options={GST_TYPE_OPTIONS}
							style={{ width: '100%' }}
							disabled={isViewMode}
							onChange={(value) => {
								setFieldValue('gst_type', value);
								// Clear GST number and file when switching to unregistered
								if (value === 'Unregistered') {
									setFieldValue('gst_number', '');
									setFieldValue('gst_file_uploaded', false);
									setGstFile(null);
									setGstFileKey('');
								}
							}}
						/>
					</FormInput>
				</Col>
				<Col span={7}>
					<FormInput
						label={values.gst_type === 'Registered' ? 'GST Number*' : 'GST Number'}
						name="gst_number"
					>
						<CustomInput
							name="gst_number"
							placeholder="Enter GST Number"
							disabled={isViewMode || values.gst_type !== 'Registered'}
						/>
					</FormInput>
				</Col>
				<Col span={9}>
					<FormInput
						name="gst_file_uploaded"
						required={values.gst_type === 'Registered'}
						style={{ marginTop: 0 }}
					>
						<Box mt={36}>
							{renderDocumentSection(
								'gst',
								gstFileKey,
								gstFile,
								gstViewing,
								gstUploading,
								setGstFile,
								setGstFileKey,
								setFieldValue,
								undefined,
								values.gst_type === 'Unregistered'
							)}
						</Box>
					</FormInput>
				</Col>
			</Row>

			{/* CRF Document */}
			<Row gutter={24} align="middle">
				<Col span={5}>
					<FormInput label="Credit Period*" name="credit_period">
						<CustomInput
							name="credit_period"
							placeholder="Enter your Credit period"
							type="number"
							style={{ marginTop: 8 }}
							disabled={isViewMode}
						/>
					</FormInput>
				</Col>
				<Col span={19}>
					<FormInput
						label="CRF File Upload*"
						name="crf_file_uploaded"
						style={{ marginTop: 0 }}
					>
						<Box mt={8.1}>
							{renderDocumentSection(
								'crf',
								crfFileKey,
								crfFile,
								crfViewing,
								crfUploading,
								setCrfFile,
								setCrfFileKey,
								setFieldValue,
								undefined
							)}
						</Box>
					</FormInput>
				</Col>
			</Row>
		</>
	);

	// Helper: Get filtered document type options (exclude already selected types in other rows)
	const getFilteredDocumentTypeOptions = (values, currentIndex) => {
		// Get all document types selected in other rows
		const selectedTypes = values.additional_documents
			?.map((doc, idx) => (idx !== currentIndex ? doc.document_type : null))
			.filter(Boolean);

		// Filter out already selected types
		return ADDITIONAL_DOCUMENT_TYPE_OPTIONS.filter(
			(option) => !selectedTypes.includes(option.value)
		);
	};

	// Helper: Render additional documents section
	const renderAdditionalDocuments = (values, setFieldValue) => (
		<Box>
			{values.additional_documents?.length > 0 && (
				<Box mb={16}>
					{values.additional_documents?.map((doc, index) => (
						<Row
							gutter={24}
							align="middle"
							// eslint-disable-next-line react/no-array-index-key
							key={`additional-doc-${index}`}
							style={{ marginBottom: 16 }}
						>
							<Col span={5}>
								<FormInput
									label="Document Type"
									name={`additional_documents[${index}].document_type`}
									required
								>
									<Select
										name={`additional_documents[${index}].document_type`}
										placeholder="Select Document Type"
										options={getFilteredDocumentTypeOptions(values, index)}
										disabled={isViewMode}
										onChange={(value) => {
											setFieldValue(
												`additional_documents[${index}].document_type`,
												value
											);
											// Clear document number when type changes
											setFieldValue(
												`additional_documents[${index}].document_number`,
												''
											);
										}}
									/>
								</FormInput>
							</Col>
							<Col span={10}>
								<FormInput
									label="Document Number"
									name={`additional_documents[${index}].document_number`}
									required
								>
									<CustomInput
										name={`additional_documents[${index}].document_number`}
										placeholder="Enter Document Number"
										disabled={isViewMode}
										onChange={(e) => {
											const { value } = e.target;
											const docType =
												values.additional_documents?.[index]?.document_type;
											// Auto-uppercase for TAN and CIN
											if (docType === 'TAN' || docType === 'CIN') {
												setFieldValue(
													`additional_documents[${index}].document_number`,
													value.toUpperCase()
												);
											} else {
												setFieldValue(
													`additional_documents[${index}].document_number`,
													value
												);
											}
										}}
									/>
								</FormInput>
							</Col>
							<Col span={!isViewMode ? 8 : 9}>
								<FormInput
									name={`additional_documents[${index}].file_uploaded`}
									required
									style={{ marginTop: 0 }}
								>
									<Box mt={36}>
										{renderDocumentSection(
											'additional',
											additionalDocFileKeys[index],
											additionalDocFiles[index],
											additionalDocViewing[index],
											additionalDocUploading[index],
											null,
											null,
											setFieldValue,
											index,
											false
										)}
									</Box>
								</FormInput>
							</Col>
							{!isViewMode && (
								<Col span={1}>
									<Box mt={-85} ml={15}>
										<Box
											display="flex"
											alignItems="center"
											justifyContent="center"
											width={36}
											height={36}
											borderRadius="50%"
											backgroundColor="#ff4d4f"
											style={{ cursor: 'pointer' }}
											onClick={() =>
												removeAdditionalDocument(
													index,
													values,
													setFieldValue
												)
											}
										>
											<MinusOutlined
												style={{
													color: '#fff',
													fontSize: '14px',
													fontWeight: 'bold',
												}}
											/>
										</Box>
									</Box>
								</Col>
							)}
						</Row>
					))}
				</Box>
			)}

			{!isViewMode && (
				<Row justify="end">
					<AddDocumentButton
						htmlType="button"
						icon={<PlusOutlined />}
						onClick={() => addAdditionalDocument(values, setFieldValue)}
						disabled={(values.additional_documents?.length || 0) >= 2}
					>
						Add Another Document
					</AddDocumentButton>
				</Row>
			)}
		</Box>
	);

	return (
		<>
			<Formik
				initialValues={initialValues}
				validationSchema={BusinessInfoSchema}
				onSubmit={handleSubmit}
				enableReinitialize
			>
				{({ values, setFieldValue, isSubmitting }) => (
					<Form>
						{/* Billing Details Section - First Container */}
						<FormContainer>
							<SectionTitle>Billing Details</SectionTitle>

							{renderBillingAddresses(values, setFieldValue)}

							{!isViewMode && (
								<Row justify="end">
									<AddButton
										htmlType="button"
										icon={<PlusOutlined />}
										onClick={() => addBillingAddress(values, setFieldValue)}
									>
										Add Another Address
									</AddButton>
								</Row>
							)}
						</FormContainer>

						{/* Proof of Business Documents Section - Second Container */}
						<FormContainer style={{ marginTop: 24 }}>
							{renderBusinessDocuments(values, setFieldValue)}
						</FormContainer>

						{/* Additional Documents - Third Container */}
						<FormContainer style={{ marginTop: 24 }}>
							{renderAdditionalDocuments(values, setFieldValue)}
						</FormContainer>

						{/* Navigation Buttons - Outside Container */}
						<Row justify="space-between" style={{ marginTop: 24 }}>
							<PreviousButton type="button" onClick={() => setCurrentStep(0)}>
								Previous
							</PreviousButton>
							<Box display="flex" style={{ gap: 16 }}>
								{isEditMode && !isViewMode && (
									<CancelButton htmlType="button" onClick={onCancel}>
										Cancel
									</CancelButton>
								)}
								{isViewMode ? (
									<NextButton
										type="button"
										onClick={() => navigate(CLIENT_LIST_ROUTE)}
									>
										Back to List
									</NextButton>
								) : (
									<SaveButton type="submit" disabled={isSubmitting}>
										{/* eslint-disable-next-line no-nested-ternary */}
										{isSubmitting
											? 'Saving...'
											: isEditMode
											? 'Save Changes'
											: 'Create'}
									</SaveButton>
								)}
							</Box>
						</Row>
					</Form>
				)}
			</Formik>

			{/* Document Viewer Modal */}
			<DocumentViewerModal
				visible={documentViewerVisible}
				onClose={() => {
					setDocumentViewerVisible(false);
					setCurrentDocumentUrl('');
					setCurrentDocumentName('');
				}}
				documentUrl={currentDocumentUrl}
				documentName={currentDocumentName}
			/>
		</>
	);
}

ClientBusinessInfoFormV2.propTypes = {
	basicInfoData: PropTypes.object,
	businessInfoData: PropTypes.object,
	setBusinessInfoData: PropTypes.func.isRequired,
	setCurrentStep: PropTypes.func.isRequired,
	isEditMode: PropTypes.bool,
	isViewMode: PropTypes.bool,
	onCancel: PropTypes.func.isRequired,
};

ClientBusinessInfoFormV2.defaultProps = {
	basicInfoData: {},
	businessInfoData: {},
	isEditMode: false,
	isViewMode: false,
};
